import type { DataSourceAbstract, FieldADTValue, RelativeSelectConfig, TableColumns, ViewField } from '@lighthouse/core'
import equal from 'fast-deep-equal'
import { useAtomCallback } from 'jotai/utils'
import { dropRepeats, find, reduce } from 'rambda'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { CURRENT_USER } from '../../../../constants'
import { useApplicationContext, useFieldBlockContext } from '../../../../contexts'
import { useAtomData } from '../../../../hooks'
import { fieldConvertValue, filterRecordsInPool, getViewColumns } from '../../../../utils'
import type { FieldBaseProps } from '../../types'
import { getConvertValue } from './help'

interface RelativeDataControllerProps extends FieldBaseProps {
    value?: string
    relativeSelectConfig: RelativeSelectConfig
}

export const useRelativeDataController = function (props: RelativeDataControllerProps) {
    const { id, onChange, value, isControlled, dataSourceList, relativeSelectConfig, config } = props
    // const { dataSourceList, userRecord } = relativeDataSource ?? {}
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()
    const { stackId, recordPoolAtom, viewIndependentDataAtomFamily } = useFieldBlockContext()
    // const userRecord = useMemo(() => find(item => item.id === USER_DATASOURCE, dataSourceList || []), [dataSourceList])
    const { relativeSelect } = relativeSelectConfig

    const {
        relativePointer = '',
        relativeFieldPointer = '',
        relativeShowFieldPointer = '',
        viewFieldSettings = [],
        filter,
        sorts,
        showMode
    } = relativeSelect ?? {}

    const viewIndependentData = useAtomData(viewIndependentDataAtomFamily({ stackId, viewId: id, dsId: relativePointer }))

    const [data, setData] = useState<string[]>([])

    const recordPoints = useMemo(() => viewIndependentData?.recordPoints ?? [], [viewIndependentData])

    const relativeDataSource = useMemo(
        () => find(dataSource => dataSource.id === relativePointer, dataSourceList || []),
        [dataSourceList, relativePointer]
    )

    const { viewOptions, schema } = relativeDataSource ?? {}

    const { tableProps = [] } = viewOptions ?? {}

    const { relativeField, relativeShowField } = useMemo(() => {
        return {
            relativeField: schema?.[relativeFieldPointer],
            relativeShowField: schema?.[relativeShowFieldPointer]
        }
    }, [relativeFieldPointer, relativeShowFieldPointer, schema])

    const getValueRecordIds = useAtomCallback(
        useCallback(
            get => {
                const recordPool = get(recordPoolAtom)
                const selfRecordIds = recordPoints.map(r => r.id)
                // 使用 filterRecordsInPool 从 Map 中过滤记录
                const relativeRecords = filterRecordsInPool(
                    recordPool,
                    r => r.dsId === relativePointer && (showMode === 'input' || selfRecordIds.includes(r.id))
                )
                return (value ?? '')
                    .toString()
                    .split(',')
                    .reduce<string[]>((preVal, label) => {
                        const record = find(record => {
                            const fieldValue = record.content?.[relativeFieldPointer]?.value?.toString() ?? ''
                            const field = schema?.[relativeFieldPointer]
                            if (!fieldValue || !field) {
                                return false
                            }
                            const newLabel = fieldConvertValue({ ...field, value: label } as FieldADTValue, {
                                personOptions,
                                isReallyNumber: true
                            }).toString()
                            const recordValue = fieldConvertValue({ ...field, value: fieldValue } as FieldADTValue, {
                                personOptions,
                                roleOptions,
                                departmentOptions,
                                isReallyNumber: true
                            }).toString()
                            return recordValue === newLabel
                        }, relativeRecords)
                        return record ? [...preVal, record.id] : preVal
                    }, [])
            },
            [
                recordPoolAtom,
                recordPoints,
                value,
                relativePointer,
                showMode,
                relativeFieldPointer,
                schema,
                personOptions,
                roleOptions,
                departmentOptions
            ]
        )
    )

    const filterViewSetting: TableColumns = useMemo(() => {
        if (!schema) {
            return tableProps
        }
        const currentColumns = getViewColumns({
            tableProps,
            value: viewFieldSettings,
            schema
        })
        return reduce<ViewField, TableColumns>(
            (preVal, curVal) => {
                const isExit = schema[curVal.fieldId]
                const fieldProp = find(item => item.id === curVal.fieldId, tableProps ?? [])
                if (!isExit || !fieldProp) {
                    return preVal
                }
                return [...preVal, { ...fieldProp, visible: curVal?.visible ?? true }]
            },
            [],
            currentColumns
        )
    }, [schema, tableProps, viewFieldSettings])

    const newRelativeDataSource: DataSourceAbstract | null = useMemo(
        () =>
            relativeDataSource && viewOptions
                ? { ...relativeDataSource, viewOptions: { ...viewOptions, sorts, filter, tableProps: filterViewSetting } }
                : null,
        [filter, filterViewSetting, relativeDataSource, sorts, viewOptions]
    )
    const handleRecordChange = useAtomCallback(
        useCallback(
            (get, set, val: string[]) => {
                const newValue = dropRepeats(val)
                if (!schema) {
                    return
                }
                const recordPool = get(recordPoolAtom)
                const saveValue = newValue.reduce<string[]>((preVal, recordId) => {
                    if (recordId.includes(CURRENT_USER.id)) {
                        return [...preVal, recordId]
                    }
                    const cellValue = getConvertValue({
                        recordId,
                        dsId: relativePointer,
                        fieldId: relativeFieldPointer,
                        records: recordPool,
                        schema,
                        personOptions,
                        roleOptions,
                        departmentOptions,
                        isReallyNumber: true
                    })

                    if (!cellValue || preVal.includes(cellValue)) {
                        return preVal
                    }
                    return [...preVal, cellValue]
                }, [])
                setData(newValue)
                onChange?.({ type: 'relativeSelect', value: saveValue.join(',') })
            },
            [departmentOptions, onChange, personOptions, recordPoolAtom, relativeFieldPointer, relativePointer, roleOptions, schema]
        )
    )

    const handleChange = useAtomCallback(
        useCallback(
            (get, set, v: string[]) => {
                setData(v)
                // // 从 recordIds 转换为 field values
                const recordPool = get(recordPoolAtom)
                const value = v.reduce<string[]>((prev, recordId) => {
                    if (recordId.includes(CURRENT_USER.id)) {
                        return [...prev, recordId]
                    }
                    const cellValue = getConvertValue({
                        recordId,
                        dsId: relativePointer,
                        fieldId: relativeFieldPointer,
                        records: recordPool,
                        schema: schema ?? {},
                        personOptions,
                        roleOptions,
                        departmentOptions,
                        isReallyNumber: true
                    })
                    if (!cellValue || prev.includes(cellValue)) {
                        return prev
                    }
                    return [...prev, cellValue]
                }, [])
                onChange?.({ type: 'relativeSelect', value: value.join(',') })
            },
            [departmentOptions, onChange, personOptions, recordPoolAtom, relativeFieldPointer, relativePointer, roleOptions, schema]
        )
    )

    useEffect(() => {
        const v = getValueRecordIds()

        if (equal(v, data)) {
            return
        }
        setData(v)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getValueRecordIds])

    return {
        onRecordChange: handleRecordChange,
        relativeField,
        relativeShowField,
        relativeDataSource: newRelativeDataSource,
        recordValue: data, // 选中的 recordIds
        recordPoints, // 所有可选的 recordPoints
        onChange: handleChange
    }
}
